import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useEscapeKey } from "@/hooks/useEscapeKey";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { useUserEntity } from "@/contexts/user-entity-context";
import { getCurrentUser } from "@/lib/api/auth";
import { getLoad, getLoadStatusLabel } from "@/lib/api/loads";
import { apiClient, ApiError } from "@/lib/api/client";
import { useToast } from "@/components/ui/toast";
import { CreateTripForm } from "@/components/load-detail/create-trip-form";
import { getLocalizedName } from "@/lib/localization";
import { fromIntlLanguage, getSystemLanguage } from "@/lib/i18n";
import { queryKeys } from "@/lib/query-keys";

import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { Surface } from "@/components/ui/surface";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { HStack } from "@/components/ui/h-stack";
import { VStack } from "@/components/ui/v-stack";
import { Separator } from "@/components/ui/separator";
import { LoadLockWarning } from "@/components/load-lock-warning";
import { TicketActivitySection } from "@/components/shared/ticket-activity-section";
import {
  LoadDetailsView,
  LoadDetailsSkeleton,
  computeLoadDetailsPolicy,
  type LoadDetailsData,
  type ViewerRole,
} from "@/components/load-detail";

function LoadDetailsPage() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const { addToast } = useToast();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tLoads } = useTranslation("loads");
  const { t: tCommon } = useTranslation("common");
  const { entity: userEntity } = useUserEntity();
  const isBroker = userEntity?.type === "Broker";
  const isCarrier = userEntity?.type === "TransportCompany";

  const {
    data: load = null,
    isLoading: loading,
    isError,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.load(params.id),
    queryFn: async () => {
      try {
        return await getLoad(params.id);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          addToast(tLoads("accessDenied"), "error");
          void navigate({ to: "/dashboard", replace: true });
          return null;
        }
        // 401 (session expired) is handled globally by the layout via auth:signout event
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    enabled: !!params.id,
  });

  const errorMessage = isError
    ? loadError instanceof Error
      ? loadError.message
      : null
    : null;

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [createTripOpen, setCreateTripOpen] = useState(false);
  const [archive, setArchive] = useState<{ open: boolean; loading: boolean }>({
    open: false,
    loading: false,
  });
  const headerMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(headerMenuRef, () => setHeaderMenuOpen(false));
  useEscapeKey(() => setHeaderMenuOpen(false));

  const showExecutorAssignment =
    (isBroker || isCarrier) && load?.status === "Negotiated" && !load?.trip;

  if (loading) {
    return (
      <VStack gap="xl" data-testid="load-details-page">
        <PageNavigationHeader
          contextLabel={tLoads("myLoads")}
          title={tCommon("loading")}
          metadata={tCommon("loading")}
          status={<Skeleton className="h-7 w-24" rounded="full" />}
          actions={<Skeleton className="h-10 w-10" rounded="md" />}
          onBack={() => router.history.back()}
          backAriaLabel={tCommon("back")}
          contextAriaLabel={tLoads("myLoads")}
        />
        <LoadDetailsSkeleton />
      </VStack>
    );
  }

  if (isError || !load) {
    return (
      <ErrorState
        title={tCommon("error")}
        description={
          errorMessage ??
          (isError ? tCommon("errorLoadingData") : tLoads("noLoadsTitle"))
        }
        action={
          <HStack align="stretch" gap="lg">
            <Button
              color="neutral"
              variant="outline"
              onClick={() => void refetch()}
            >
              {tCommon("retry")}
            </Button>
            <Button
              color="neutral"
              variant="outline"
              onClick={() => router.history.back()}
            >
              <Icon.ChevronLeft size="sm" className="mr-2" />
              {tCommon("back")}
            </Button>
          </HStack>
        }
        className="h-96"
      />
    );
  }

  const statusLabel = getLoadStatusLabel(load.status, tLoads);
  const hasPodAction = load.status === "Delivered";
  const createdAtLabel = new Date(load.created_at).toLocaleDateString(
    getSystemLanguage(language),
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
  );
  const user = getCurrentUser();
  const isAdmin = user?.is_admin === true;

  const viewerRole: ViewerRole = isAdmin
    ? "admin"
    : load.is_editable
      ? "owner"
      : isBroker
        ? "broker"
        : userEntity?.type === "TransportCompany"
          ? "carrier"
          : "owner";

  const policy = computeLoadDetailsPolicy({
    viewerRole,
    status: load.status,
    hasTrip: Boolean(load.trip),
  });

  const handleCopyLink = async () => {
    setHeaderMenuOpen(false);
    try {
      await navigator.clipboard.writeText(window.location.href);
      addToast(tLoads("linkCopied"), "success");
    } catch {
      addToast(tCommon("error"), "error");
    }
  };

  const handleArchiveRequest = () => {
    setHeaderMenuOpen(false);
    setArchive({ open: true, loading: false });
  };

  const handleArchiveConfirm = async () => {
    setArchive({ open: true, loading: true });
    try {
      await apiClient.delete(`/loads/${load.id}`);
      addToast(tLoads("loadArchived"), "success");
      setArchive({ open: false, loading: false });
      void navigate({ to: "/loads" });
    } catch (err) {
      console.error("Error archiving load:", err);
      addToast(tCommon("error"), "error");
      setArchive({ open: false, loading: false });
    }
  };

  return (
    <VStack gap="xl" data-testid="load-details-page">
      <PageNavigationHeader
        contextLabel={tLoads("myLoads")}
        title={`${load.load_id} · ${getLocalizedName(load.load_type, language)}`}
        metadata={`${tLoads("created")} ${createdAtLabel}`}
        status={
          <EntityStatusBadge
            entityType="load"
            status={load.status}
            className="px-3 py-1"
          >
            {statusLabel}
          </EntityStatusBadge>
        }
        actions={
          <HStack ref={headerMenuRef} gap="sm" className="relative">
            {showExecutorAssignment ? (
              <Button
                type="button"
                size="md"
                className="h-10 px-6"
                onClick={() => setCreateTripOpen(true)}
                data-testid="load-header-create-trip-btn"
              >
                {isCarrier ? tLoads("createTrip") : tLoads("assignExecutor")}
              </Button>
            ) : null}

            {/* TODO(yuklar-0t9.5): View POD button hidden — no GET /loads/:id/pod
                endpoint or viewer route exists. Restore the button below + wire
                onClick once the backend POD endpoint lands. */}
            {/* eslint-disable-next-line no-constant-condition, no-constant-binary-expression -- TODO above */}
            {false && hasPodAction ? (
              <Button
                type="button"
                color="neutral"
                variant="outline"
                size="md"
                className="h-10 px-6"
                data-testid="load-header-view-pod-btn"
              >
                {tLoads("viewPOD")}
              </Button>
            ) : null}

            <Button
              type="button"
              color="neutral"
              variant="outline"
              size="md"
              className="h-10 w-10 !p-0"
              aria-haspopup="menu"
              aria-expanded={headerMenuOpen}
              aria-label={tCommon("actions")}
              data-testid="load-header-actions-btn"
              onClick={() => setHeaderMenuOpen((prev) => !prev)}
            >
              <Icon.MoreHorizontal size="md" />
            </Button>

            {headerMenuOpen ? (
              <Surface
                role="menu"
                aria-orientation="vertical"
                bg="container"
                border="default"
                rounded="2xl"
                padding="lg"
                className="absolute right-0 top-12 z-header w-80 shadow-lg"
                data-testid="load-header-actions-menu"
              >
                <Button
                  variant="ghost"
                  role="menuitem"
                  className="w-full justify-start rounded-lg px-3 py-2 text-xl font-semibold"
                  onClick={handleCopyLink}
                  data-testid="load-header-copy-link-btn"
                >
                  {tLoads("copyLink")}
                </Button>
                {/* TODO(yuklar-0t9.5): Download Documents hidden — no
                    documents-bundle endpoint exists (only get-agreement-pdf).
                    Restore once a per-load documents endpoint lands. */}
                {/* TODO(yuklar-0t9.5): Duplicate Load hidden — no
                    POST /loads/duplicate endpoint and the create page does not
                    yet read a prefillFromId param. Restore once one of those
                    paths is implemented. */}

                {load.is_deletable ? (
                  <>
                    <Separator spacing="md" />
                    <Button
                      variant="ghost"
                      role="menuitem"
                      className="w-full justify-start rounded-lg px-3 py-2 text-xl font-semibold"
                      onClick={handleArchiveRequest}
                      data-testid="load-header-archive-btn"
                    >
                      {tLoads("archiveLoad")}
                    </Button>
                  </>
                ) : null}
              </Surface>
            ) : null}
          </HStack>
        }
        onBack={() => router.history.back()}
        backAriaLabel={tCommon("back")}
        contextAriaLabel={tLoads("myLoads")}
      />

      <TicketActivitySection
        tickets={load.tickets ?? []}
        currentUserId={user?.id}
        onTicketAction={() => void refetch()}
      />

      {load.trip ? (
        <LoadLockWarning
          tripId={load.id}
          tripStatus={getLoadStatusLabel(load.status, tLoads)}
          reason={tLoads("lockedReasonActiveTrip", {
            status: getLoadStatusLabel(load.status, tLoads),
          })}
          onViewTrip={() =>
            void navigate({ to: "/trips/$id", params: { id: load.id } })
          }
          data-testid="load-detail-lock-warning"
        />
      ) : null}

      <LoadDetailsView
        data={load as unknown as LoadDetailsData}
        policy={policy}
      />

      <CreateTripForm
        loadId={load.id}
        isBroker={isBroker}
        isCarrier={isCarrier}
        open={createTripOpen}
        onClose={() => setCreateTripOpen(false)}
        onSuccess={() => void refetch()}
      />

      <ConfirmDialog
        open={archive.open}
        title={tLoads("archiveLoad")}
        description={tLoads("confirmArchive")}
        confirmLabel={tLoads("archiveLoad")}
        cancelLabel={tCommon("cancel")}
        loading={archive.loading}
        onConfirm={() => void handleArchiveConfirm()}
        onCancel={() => setArchive({ open: false, loading: false })}
      />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/loads/$id/")({
  component: LoadDetailsPage,
});
